import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DISC = { D: "Dominance (D)", I: "Influence (I)", S: "Steadiness (S)", C: "Conscientiousness (C)" };

const discTop2 = (v) => {
  const s = v.disc_scores || {};
  const order = (v.disc_profile || "").split("/").filter((x) => x in s);
  Object.keys(s).sort((a, b) => s[b] - s[a]).forEach((k) => { if (!order.includes(k)) order.push(k); });
  return order.slice(0, 2);
};

const Field = ({ n, label, children, testid }) => (
  <div className="flex gap-4 py-2.5 border-b border-[var(--line)] text-sm" data-testid={testid}>
    <span className="font-mono-dt text-muted-ink w-6">{n}</span>
    <span className="text-muted-ink w-48 shrink-0">{label}</span>
    <span className="text-navy">{children}</span>
  </div>
);

export const ApprovalCard = ({ v, setV, canEdit }) => {
  const [teams, setTeams] = useState([]);
  const [pick, setPick] = useState(v.approval?.team_id || "none");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)); }, []);
  const [p1, p2] = discTop2(v);
  const t5 = v.top5 || [];

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/volunteers/${v.volunteer_id}/approval`, { team_id: pick === "none" ? null : pick });
      setV((p) => ({ ...p, approval: data.approval, approval_history: data.approval_history, ...(data.sync || {}) }));
      data.sync?.pco_status === "error" ? toast.error(`Saved, but Planning Center: ${data.sync.pco_error}`) : toast.success(data.sync ? "Approval saved and synced to Planning Center" : "Approval saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-soft p-8" data-testid="approval-card">
      <p className="font-display text-navy text-xl font-semibold">Ministry placement</p>
      <p className="text-sm text-muted-ink mt-1">Recommendations are a guide. Placement only happens when an admin approves it.</p>
      <div className="mt-5">
        <Field n="1" label="Top Spiritual Gift" testid="field-top-gift">{v.top_gifts?.[0] || "—"}</Field>
        <Field n="2" label="Personality Type 1" testid="field-p1">{DISC[p1] || "—"}</Field>
        <Field n="3" label="Personality Type 2" testid="field-p2">{DISC[p2] || "—"}</Field>
        <Field n="4" label="Ministry Match 1" testid="field-m1">{t5[0]?.name || "—"}</Field>
        <Field n="5" label="Ministry Match 2" testid="field-m2">{t5[1]?.name || "—"}</Field>
        <Field n="6" label="Top 5 Ministry Matches" testid="field-top5">
          <ol className="space-y-0.5">{t5.map((m, i) => <li key={m.team_id}>{i + 1}. {m.name} — {m.score}%</li>)}</ol>
        </Field>
        <Field n="7" label="Preferred Ministry" testid="field-preferred">{v.preferred?.name || "No preference yet"}</Field>
        <Field n="8" label="Approved Ministry" testid="field-approved">
          {v.approval ? <>{v.approval.name} <span className="text-muted-ink">· by {v.approval.by} · {new Date(v.approval.at).toLocaleString()}</span></> : "Not approved yet"}
        </Field>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <div className="w-72">
            <Select value={pick} onValueChange={setPick} disabled={busy}>
              <SelectTrigger className="h-11" data-testid="approval-team-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No approved ministry</SelectItem>
                {teams.map((t) => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button onClick={save} disabled={busy || pick === (v.approval?.team_id || "none")} className="btn-navy !py-2.5" data-testid="approval-save-btn">{busy ? "Saving..." : "Save approval"}</button>
        </div>
      )}
      {v.approval_history?.length > 0 && (
        <details className="mt-5 text-sm" data-testid="approval-history">
          <summary className="cursor-pointer text-muted-ink">Approval history ({v.approval_history.length})</summary>
          <ul className="mt-2 space-y-1">{[...v.approval_history].reverse().map((h) => <li key={h.at} className="text-muted-ink">{new Date(h.at).toLocaleString()} · {h.by} · {h.team || "Cleared"}</li>)}</ul>
        </details>
      )}
    </section>
  );
};
